// Bloques B/C/E/G-S de 018A (Fase 1B, Tanda 14, ver PLAN.md) — cierra 018A completo.
// Bloques A/D/F ya existen (Tandas 12/13). Todo acá es `Par[]` puro (sin
// `Observation`) salvo los 2 diagnósticos del bloque B (preoperatorio/
// postoperatorio), que SÍ crean `Condition` — por eso ese par de campos vive
// como estado aparte en `Formulario018A.tsx` (tipo `DiagnosticoCie10Valor` de
// `SelectorDiagnosticoCie10.tsx`), igual que `Formulario038.tsx`, y NO están
// en `EstadoBloquesRestantes018A`.
//
// Decisiones de diseño (ambigüedad de producto sin impacto clínico, PLAN.md
// no las resuelve — se deciden y se marcan, mismo criterio de toda la Fase 1B):
// - E (Drogas administradas) no trae columnas en la fuente — se usan
//   hora/droga/dosis/vía, el mínimo estándar de un registro de fármacos.
// - H/I (reposición volémica/pérdidas) no se persisten como `Observation`
//   (mismo criterio que los 7 parámetros del gráfico de D: sin consumidor
//   real hoy). Los totales y el balance (reposición − pérdidas) se calculan,
//   no se guardan — mismo patrón que `calcularBalanceHidrico` de 020.
// - J (recién nacido) es una limitación conocida y documentada: el neonato
//   es un `Patient` distinto, fuera de alcance crear uno nuevo acá — el
//   APGAR queda como `Par[]` de respaldo en el PDF de la madre.
// - S (profesional responsable) es solo paridad de campo impreso — la firma
//   real ya la resuelve `PanelFirmaImpresion` después de guardar, así que acá
//   solo se piden hora/nombre/código, sin duplicar firma/sello.
import { imcTexto } from './calculosClinicosDatos';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

// ---------- A (extras) — fecha, talla, peso, IMC, grupo y factor ----------
// El encabezado A del papel trae, además de los datos del paciente (`EncabezadoMspCampos`), una fila con FECHA, TALLA (cm), PESO (kg),
// IMC, GRUPO Y FACTOR y CONSENTIMIENTO INFORMADO (este último vive en `Formulario018A.tsx`). El IMC no se teclea: se calcula
// (`imcTexto`, mismo criterio que 002/003/113). "Grupo y factor" es una sola celda en el papel; se captura como 2 listas cerradas
// (A/B/AB/O y Rh +/-) y se imprime junta ("O Rh+"), sin permitir texto libre que se preste a errores de transcripción.

export const GRUPOS_SANGUINEOS = ['A', 'B', 'AB', 'O'] as const;
export type GrupoSanguineo = (typeof GRUPOS_SANGUINEOS)[number];
export const FACTORES_RH = ['+', '-'] as const;
export type FactorRh = (typeof FACTORES_RH)[number];

export interface EstadoBloqueA {
  /** Fecha del procedimiento (aaaa-mm-dd). */
  readonly fecha: string;
  readonly talla: string;
  readonly peso: string;
  readonly grupo: GrupoSanguineo | '';
  readonly factor: FactorRh | '';
}

export function estadoInicialBloqueA(): EstadoBloqueA {
  return { fecha: '', talla: '', peso: '', grupo: '', factor: '' };
}

/** "O Rh+", "A Rh-"; si solo se eligió una de las dos partes, imprime esa. */
export function grupoYFactorTexto(grupo: string, factor: string): string {
  return [grupo, factor ? `Rh${factor}` : ''].filter((v) => v !== '').join(' ');
}

export function paresDeBloqueA(t: Traducir, e: EstadoBloqueA): Par[] {
  return [
    { pregunta: t('formularios.f018a.bloqueA.fecha'), respuesta: e.fecha.trim() },
    { pregunta: t('formularios.f018a.bloqueA.talla'), respuesta: e.talla.trim() },
    { pregunta: t('formularios.f018a.bloqueA.peso'), respuesta: e.peso.trim() },
    { pregunta: t('formularios.f018a.bloqueA.imc'), respuesta: imcTexto(e.peso, e.talla) },
    { pregunta: t('formularios.f018a.bloqueA.grupoYFactor'), respuesta: grupoYFactorTexto(e.grupo, e.factor) },
  ].filter((p) => p.respuesta !== '');
}

// ---------- C / L / M / N — checklists (Record<clave, boolean>, mismo tipo que ChecklistGrid) ----------

export const REGION_OPERATORIA_OPCIONES = ['cabeza', 'organosSentidos', 'cuello', 'columna', 'torax', 'abdomen', 'pelvis', 'extremidadesSuperiores', 'extremidadesInferiores', 'perineal'] as const;
export const TECNICAS_ESPECIALES_OPCIONES = ['hemodilucion', 'autotransfusion', 'hipotension', 'hipotermia', 'circulacionExtracorporea'] as const;
export const TEMPERATURA_OPCIONES = ['mantaTermica', 'calentamientoFluidos'] as const;
export const INCIDENTES_OPCIONES = [
  'actividadElectricaSinPulso',
  'arritmia',
  'asistolia',
  'bradicardiaInestable',
  'tromboemboliaPulmonar',
  'hipertermiaMaligna',
  'anafilaxia',
  'isquemiaMiocardica',
  'hipoxemia',
  'neumotorax',
  'broncoespasmo',
  'despertarProlongado',
  'emboliaAereaVenosa',
  'reaccionTransfusion',
  'laringoespasmo',
  'dificultadTecnica',
  'otros',
] as const;

function paresDeChecklist(t: Traducir, prefijoI18n: string, opciones: readonly string[], valores: Record<string, boolean>): Par {
  return { pregunta: t(`${prefijoI18n}.titulo`), respuesta: opciones.filter((c) => valores[c]).map((c) => t(`${prefijoI18n}.opciones.${c}`)).join(', ') };
}

// ---------- B — Servicio y prioridad (sin los 2 diagnósticos, ver arriba) ----------

export type PrioridadCirugia = 'emergente' | 'urgente' | 'electivo';
export const PRIORIDAD_OPCIONES: readonly PrioridadCirugia[] = ['emergente', 'urgente', 'electivo'];

// El papel imprime "MAS 1" (un renglón adicional) junto a los 4 renglones principales del bloque B: diagnóstico preoperatorio,
// diagnóstico postoperatorio, cirugía propuesta y cirugía realizada. Los 4 tienen su campo adicional.
export interface EstadoBloqueB {
  readonly diagnosticoPreoperatorioAdicional: string;
  readonly diagnosticoPostoperatorioAdicional: string;
  readonly cirugiaPropuesta: string;
  readonly cirugiaPropuestaAdicional: string;
  readonly especialidadServicio: string;
  readonly prioridad: PrioridadCirugia | '';
  readonly cirugiaRealizada: string;
  readonly cirugiaRealizadaAdicional: string;
  readonly anestesiologo: string;
  readonly ayudantes: string;
  /** El papel trae dos renglones "AYUDANTE (S)"; `ayudantes` es el primero. */
  readonly ayudantes2: string;
  readonly instrumentista: string;
  readonly quirofano: string;
  readonly cirujano: string;
  readonly circulante: string;
  readonly otros: string;
}

export function estadoInicialBloqueB(): EstadoBloqueB {
  return {
    diagnosticoPreoperatorioAdicional: '',
    diagnosticoPostoperatorioAdicional: '',
    cirugiaPropuesta: '',
    cirugiaPropuestaAdicional: '',
    especialidadServicio: '',
    prioridad: '',
    cirugiaRealizada: '',
    cirugiaRealizadaAdicional: '',
    anestesiologo: '',
    ayudantes: '',
    ayudantes2: '',
    instrumentista: '',
    quirofano: '',
    cirujano: '',
    circulante: '',
    otros: '',
  };
}

function paresDeBloqueB(t: Traducir, e: EstadoBloqueB): Par[] {
  return [
    { pregunta: t('formularios.f018a.bloqueB.diagnosticoPreoperatorioAdicional'), respuesta: e.diagnosticoPreoperatorioAdicional.trim() },
    { pregunta: t('formularios.f018a.bloqueB.diagnosticoPostoperatorioAdicional'), respuesta: e.diagnosticoPostoperatorioAdicional.trim() },
    { pregunta: t('formularios.f018a.bloqueB.cirugiaPropuesta'), respuesta: e.cirugiaPropuesta.trim() },
    { pregunta: t('formularios.f018a.bloqueB.cirugiaPropuestaAdicional'), respuesta: e.cirugiaPropuestaAdicional.trim() },
    { pregunta: t('formularios.f018a.bloqueB.especialidadServicio'), respuesta: e.especialidadServicio.trim() },
    { pregunta: t('formularios.f018a.bloqueB.prioridad'), respuesta: e.prioridad ? t(`formularios.f018a.bloqueB.prioridadOpciones.${e.prioridad}`) : '' },
    { pregunta: t('formularios.f018a.bloqueB.cirugiaRealizada'), respuesta: e.cirugiaRealizada.trim() },
    { pregunta: t('formularios.f018a.bloqueB.cirugiaRealizadaAdicional'), respuesta: e.cirugiaRealizadaAdicional.trim() },
    { pregunta: t('formularios.f018a.bloqueB.anestesiologo'), respuesta: e.anestesiologo.trim() },
    { pregunta: t('formularios.f018a.bloqueB.ayudantes'), respuesta: e.ayudantes.trim() },
    { pregunta: t('formularios.f018a.bloqueB.ayudantes2'), respuesta: e.ayudantes2.trim() },
    { pregunta: t('formularios.f018a.bloqueB.instrumentista'), respuesta: e.instrumentista.trim() },
    { pregunta: t('formularios.f018a.bloqueB.quirofano'), respuesta: e.quirofano.trim() },
    { pregunta: t('formularios.f018a.bloqueB.cirujano'), respuesta: e.cirujano.trim() },
    { pregunta: t('formularios.f018a.bloqueB.circulante'), respuesta: e.circulante.trim() },
    { pregunta: t('formularios.f018a.bloqueB.otros'), respuesta: e.otros.trim() },
  ];
}

// ---------- E — Drogas administradas (tabla repetible, 24 filas) ----------

export interface FilaDroga {
  readonly id: string;
  readonly hora: string;
  readonly droga: string;
  readonly dosis: string;
  readonly via: string;
}

export function filaDrogaVacia(): FilaDroga {
  return { id: crypto.randomUUID(), hora: '', droga: '', dosis: '', via: '' };
}

export const TOPE_FILAS_DROGAS = 24;

// ---------- G — Accesos vasculares (filas fijas, sin agregar/quitar) ----------

export const FILAS_ACCESOS_VASCULARES = ['ivPeriferico1', 'ivPeriferico2', 'ivPeriferico3', 'ivCentral', 'intraArterial', 'otro'] as const;
export type FilaAccesoVascular = (typeof FILAS_ACCESOS_VASCULARES)[number];

export interface AccesoVascular {
  readonly tipo: string;
  readonly calibre: string;
  readonly sitio: string;
}

export type AccesosVasculares = Record<FilaAccesoVascular, AccesoVascular>;

function accesoVascularVacio(): AccesoVascular {
  return { tipo: '', calibre: '', sitio: '' };
}

export function estadoInicialAccesosVasculares(): AccesosVasculares {
  return Object.fromEntries(FILAS_ACCESOS_VASCULARES.map((f) => [f, accesoVascularVacio()])) as AccesosVasculares;
}

// ---------- H — Reposición volémica (mL) ----------

export const CAMPOS_REPOSICION = ['dextrosa5', 'dextrosa10', 'dextrosa50', 'dextrosaEnSs', 'ss09', 'lactatoRinger', 'expansores', 'sangre', 'plasma', 'plaquetas', 'crioprecipitados', 'otros'] as const;
export type CampoReposicion = (typeof CAMPOS_REPOSICION)[number];
export type ReposicionVolemica = Record<CampoReposicion, string>;

export function estadoInicialReposicion(): ReposicionVolemica {
  return Object.fromEntries(CAMPOS_REPOSICION.map((c) => [c, ''])) as ReposicionVolemica;
}

// ---------- I — Pérdidas ----------

export const CAMPOS_PERDIDAS = ['sangrado', 'diuresis', 'otros'] as const;
export type CampoPerdidas = (typeof CAMPOS_PERDIDAS)[number];
export type Perdidas = Record<CampoPerdidas, string>;

export function estadoInicialPerdidas(): Perdidas {
  return Object.fromEntries(CAMPOS_PERDIDAS.map((c) => [c, ''])) as Perdidas;
}

function sumaMl(valores: readonly string[]): number {
  return valores.reduce((acc, v) => acc + (Number(v) || 0), 0);
}

// ---------- O — Laboratorio (tabla repetible, 10 filas) ----------

export interface FilaLaboratorio {
  readonly id: string;
  readonly hora: string;
  readonly ph: string;
  readonly po2: string;
  readonly pco2: string;
  readonly hco3: string;
  readonly eb: string;
  readonly sato2: string;
  readonly lactato: string;
  readonly glucosa: string;
  readonly na: string;
  readonly k: string;
  readonly cl: string;
  readonly hcto: string;
  readonly hb: string;
  readonly otro: string;
}

export const COLUMNAS_LABORATORIO = ['hora', 'ph', 'po2', 'pco2', 'hco3', 'eb', 'sato2', 'lactato', 'glucosa', 'na', 'k', 'cl', 'hcto', 'hb', 'otro'] as const satisfies readonly (keyof Omit<FilaLaboratorio, 'id'>)[];

export function filaLaboratorioVacia(): FilaLaboratorio {
  return { id: crypto.randomUUID(), hora: '', ph: '', po2: '', pco2: '', hco3: '', eb: '', sato2: '', lactato: '', glucosa: '', na: '', k: '', cl: '', hcto: '', hb: '', otro: '' };
}

export const TOPE_FILAS_LABORATORIO = 10;

// ---------- R — Condición de egreso ----------

export type ExtubadoIntubado = 'extubado' | 'intubado';
export const EXTUBADO_INTUBADO_OPCIONES: readonly ExtubadoIntubado[] = ['extubado', 'intubado'];

export type ConducidoA = 'ucpa' | 'uci' | 'criticos-emergencia' | 'morgue';
export const CONDUCIDO_A_OPCIONES: readonly ConducidoA[] = ['ucpa', 'uci', 'criticos-emergencia', 'morgue'];

export interface ConstantesEgreso {
  readonly ta: string;
  readonly fc: string;
  readonly fr: string;
  readonly sato2: string;
  readonly temperatura: string;
}

export function estadoInicialConstantesEgreso(): ConstantesEgreso {
  return { ta: '', fc: '', fr: '', sato2: '', temperatura: '' };
}

// ---------- Estado combinado B(sin diagnósticos)/C/E/G-S ----------

export const APGAR_OPCIONES: readonly string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

export interface EstadoBloquesRestantes018A {
  readonly a: EstadoBloqueA;
  readonly b: EstadoBloqueB;
  readonly regionOperatoria: Record<string, boolean>;
  readonly drogas: readonly FilaDroga[];
  readonly accesosVasculares: AccesosVasculares;
  readonly reposicion: ReposicionVolemica;
  readonly perdidas: Perdidas;
  readonly fetoMuerto: boolean;
  readonly apgar1: string;
  readonly apgar5: string;
  readonly apgar10: string;
  readonly duracionAnestesia: string;
  readonly duracionCirugia: string;
  readonly tecnicasEspeciales: Record<string, boolean>;
  readonly temperatura: Record<string, boolean>;
  readonly temperaturaOtros: string;
  readonly incidentes: Record<string, boolean>;
  readonly laboratorio: readonly FilaLaboratorio[];
  readonly observaciones: string;
  readonly extubadoIntubado: ExtubadoIntubado | '';
  readonly conducidoA: ConducidoA | '';
  readonly constantesEgreso: ConstantesEgreso;
  readonly responsableHora: string;
  readonly responsableNombre: string;
  readonly responsableCodigo: string;
}

export function estadoInicialBloquesRestantes018A(): EstadoBloquesRestantes018A {
  return {
    a: estadoInicialBloqueA(),
    b: estadoInicialBloqueB(),
    regionOperatoria: {},
    drogas: [],
    accesosVasculares: estadoInicialAccesosVasculares(),
    reposicion: estadoInicialReposicion(),
    perdidas: estadoInicialPerdidas(),
    fetoMuerto: false,
    apgar1: '',
    apgar5: '',
    apgar10: '',
    duracionAnestesia: '',
    duracionCirugia: '',
    tecnicasEspeciales: {},
    temperatura: {},
    temperaturaOtros: '',
    incidentes: {},
    laboratorio: [],
    observaciones: '',
    extubadoIntubado: '',
    conducidoA: '',
    constantesEgreso: estadoInicialConstantesEgreso(),
    responsableHora: '',
    responsableNombre: '',
    responsableCodigo: '',
  };
}

function paresDeAccesosVasculares(t: Traducir, e: AccesosVasculares): Par[] {
  return FILAS_ACCESOS_VASCULARES.map((fila) => ({
    pregunta: t(`formularios.f018a.bloqueG.filas.${fila}`),
    respuesta: [e[fila].tipo, e[fila].calibre, e[fila].sitio].filter((v) => v.trim() !== '').join(' | '),
  }));
}

function paresDeReposicionYPerdidas(t: Traducir, reposicion: ReposicionVolemica, perdidas: Perdidas): Par[] {
  const totalReposicion = sumaMl(CAMPOS_REPOSICION.map((c) => reposicion[c]));
  const totalPerdidas = sumaMl(CAMPOS_PERDIDAS.map((c) => perdidas[c]));
  return [
    ...CAMPOS_REPOSICION.map((c) => ({ pregunta: t(`formularios.f018a.bloqueH.campos.${c}`), respuesta: reposicion[c].trim() })),
    { pregunta: t('formularios.f018a.bloqueH.total'), respuesta: totalReposicion > 0 ? String(totalReposicion) : '' },
    ...CAMPOS_PERDIDAS.map((c) => ({ pregunta: t(`formularios.f018a.bloqueI.campos.${c}`), respuesta: perdidas[c].trim() })),
    { pregunta: t('formularios.f018a.bloqueI.total'), respuesta: totalPerdidas > 0 ? String(totalPerdidas) : '' },
    { pregunta: t('formularios.f018a.bloqueI.balance'), respuesta: totalReposicion > 0 || totalPerdidas > 0 ? String(totalReposicion - totalPerdidas) : '' },
  ];
}

/** Serializa los bloques A (fecha/talla/peso/IMC/grupo), B (sin los diagnósticos), C/E/G-S a `Par[]`, listos para `onGuardado`. */
export function paresDeBloquesRestantes018A(t: Traducir, estado: EstadoBloquesRestantes018A): Par[] {
  const pares: Par[] = [
    ...paresDeBloqueA(t, estado.a),
    ...paresDeBloqueB(t, estado.b),
    paresDeChecklist(t, 'formularios.f018a.bloqueC.region', REGION_OPERATORIA_OPCIONES, estado.regionOperatoria),
    ...estado.drogas.map((f, i) => ({
      pregunta: `${t('formularios.f018a.bloqueE.titulo')} #${i + 1}`,
      respuesta: [f.hora, f.droga, f.dosis, f.via].filter((v) => v.trim() !== '').join(' | '),
    })),
    ...paresDeAccesosVasculares(t, estado.accesosVasculares),
    ...paresDeReposicionYPerdidas(t, estado.reposicion, estado.perdidas),
    { pregunta: t('formularios.f018a.bloqueJ.fetoMuerto'), respuesta: estado.fetoMuerto ? t('formulario.si') : '' },
    { pregunta: t('formularios.f018a.bloqueJ.apgar1'), respuesta: estado.apgar1 },
    { pregunta: t('formularios.f018a.bloqueJ.apgar5'), respuesta: estado.apgar5 },
    { pregunta: t('formularios.f018a.bloqueJ.apgar10'), respuesta: estado.apgar10 },
    { pregunta: t('formularios.f018a.bloqueK.duracionAnestesia'), respuesta: estado.duracionAnestesia.trim() },
    { pregunta: t('formularios.f018a.bloqueK.duracionCirugia'), respuesta: estado.duracionCirugia.trim() },
    paresDeChecklist(t, 'formularios.f018a.bloqueL.tecnicas', TECNICAS_ESPECIALES_OPCIONES, estado.tecnicasEspeciales),
    paresDeChecklist(t, 'formularios.f018a.bloqueM.mantenimiento', TEMPERATURA_OPCIONES, estado.temperatura),
    { pregunta: t('formularios.f018a.bloqueM.otros'), respuesta: estado.temperaturaOtros.trim() },
    paresDeChecklist(t, 'formularios.f018a.bloqueN.incidentes', INCIDENTES_OPCIONES, estado.incidentes),
    ...estado.laboratorio.map((f, i) => ({
      pregunta: `${t('formularios.f018a.bloqueO.titulo')} #${i + 1}`,
      respuesta: COLUMNAS_LABORATORIO.map((c) => f[c]).filter((v) => v.trim() !== '').join(' | '),
    })),
    { pregunta: t('formularios.f018a.bloqueP.observaciones'), respuesta: estado.observaciones.trim() },
    { pregunta: t('formularios.f018a.bloqueR.extubadoIntubado'), respuesta: estado.extubadoIntubado ? t(`formularios.f018a.bloqueR.extubadoIntubadoOpciones.${estado.extubadoIntubado}`) : '' },
    { pregunta: t('formularios.f018a.bloqueR.conducidoA'), respuesta: estado.conducidoA ? t(`formularios.f018a.bloqueR.conducidoAOpciones.${estado.conducidoA}`) : '' },
    {
      pregunta: t('formularios.f018a.bloqueR.constantesEgreso'),
      respuesta: [estado.constantesEgreso.ta, estado.constantesEgreso.fc, estado.constantesEgreso.fr, estado.constantesEgreso.sato2, estado.constantesEgreso.temperatura].filter((v) => v.trim() !== '').join(' | '),
    },
    { pregunta: t('formularios.f018a.bloqueS.hora'), respuesta: estado.responsableHora.trim() },
    { pregunta: t('formularios.f018a.bloqueS.nombre'), respuesta: estado.responsableNombre.trim() },
    { pregunta: t('formularios.f018a.bloqueS.codigo'), respuesta: estado.responsableCodigo.trim() },
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
